import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const offices = [
  {
    city: "Mumbai",
    lines: ["Office No. 303, 3rd Floor,", "Rajendra Chambers, 19 Nanabhai Lane,", "Fort, Mumbai – 400001"],
    map: "https://www.google.com/maps?q=Rajendra+Chambers+Nanabhai+Lane+Fort+Mumbai&output=embed",
  },
  {
    city: "Kolhapur",
    lines: ["Arunodaya Apartment, 1st Floor,", "Azad Chowk, Near Commerce College,", "Kolhapur – 416001"],
    map: "https://www.google.com/maps?q=Azad+Chowk+Kolhapur&output=embed",
  },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry received. The chamber will revert shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Could not submit. Please try again or call directly.");
    } finally { setLoading(false); }
  };

  return (
    <section id="contact" className="py-24 md:py-40 bg-[#0B0B0B] border-t border-white/5" data-testid="contact-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-24">
        <div className="mb-16 md:mb-20 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-6">
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05] text-white/95">Two chambers.<br/><span className="italic text-[#C8A45D]/90">One practice.</span></h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 self-end text-white/55 font-body text-base leading-relaxed">
            Consultations are by appointment. For urgent matters, please call or send a WhatsApp message directly.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 mb-20">
          {offices.map((o) => (
            <motion.div
              key={o.city}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-[#0B0B0B] p-8 md:p-12"
              data-testid={`office-${o.city.toLowerCase()}`}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="font-cinzel text-[10px] tracking-[0.35em] text-[#C8A45D]">{o.city.toUpperCase()} · CHAMBER</div>
                <MapPin size={16} className="text-[#C8A45D]" strokeWidth={1.3}/>
              </div>
              <div className="font-serif-display text-3xl md:text-4xl text-white/95 mb-6">{o.city}</div>
              <div className="text-white/60 font-body text-sm space-y-1 mb-8">
                {o.lines.map((l, i) => <div key={i}>{l}</div>)}
              </div>
              <div className="aspect-[16/9] w-full overflow-hidden mb-6 border border-white/10">
                <iframe src={o.map} title={o.city} className="w-full h-full grayscale contrast-125 opacity-80" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Direct contact */}
          <div className="lg:col-span-5 space-y-6">
            <div className="chapter-num mb-4">Direct</div>
            <a href="tel:+917666753201" className="flex items-center gap-4 group border-b border-white/10 pb-5" data-testid="contact-phone">
              <Phone className="text-[#C8A45D]" size={18} strokeWidth={1.3}/>
              <div>
                <div className="text-[10px] font-cinzel tracking-[0.3em] text-white/40">TELEPHONE</div>
                <div className="font-serif-display text-2xl text-white group-hover:text-[#C8A45D] transition-colors">+91 76667 53201</div>
              </div>
            </a>
            <a href="https://wa.me/917666753201" target="_blank" rel="noreferrer" className="flex items-center gap-4 group border-b border-white/10 pb-5" data-testid="contact-whatsapp">
              <Send className="text-[#C8A45D]" size={18} strokeWidth={1.3}/>
              <div>
                <div className="text-[10px] font-cinzel tracking-[0.3em] text-white/40">WHATSAPP</div>
                <div className="font-serif-display text-2xl text-white group-hover:text-[#C8A45D] transition-colors">+91 7666753201</div>
              </div>
            </a>
            <a href="mailto:adv.harshavardhanpatil@gmail.com" className="flex items-center gap-4 group border-b border-white/10 pb-5" data-testid="contact-email">
              <Mail className="text-[#C8A45D]" size={18} strokeWidth={1.3}/>
              <div>
                <div className="text-[10px] font-cinzel tracking-[0.3em] text-white/40">EMAIL</div>
                <div className="font-serif-display text-lg md:text-xl text-white group-hover:text-[#C8A45D] transition-colors break-all">adv.harshavardhanpatil@gmail.com</div>
              </div>
            </a>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-7 space-y-4" data-testid="enquiry-form">
            <div className="chapter-num mb-4">Enquiry</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input required className="nx-input" placeholder="Full Name *" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="input-name"/>
              <input required type="email" className="nx-input" placeholder="Email *" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} data-testid="input-email"/>
              <input required className="nx-input" placeholder="Phone *" value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} data-testid="input-phone"/>
              <input className="nx-input" placeholder="Subject / Matter" value={form.subject} onChange={(e)=>setForm({...form, subject:e.target.value})} data-testid="input-subject"/>
            </div>
            <textarea required className="nx-input" rows="5" placeholder="Brief note of the matter *" value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})} data-testid="input-message"/>
            <div className="pt-4">
              <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60" data-testid="submit-enquiry">
                {loading ? "Sending…" : "Submit Enquiry"} <Send size={14}/>
              </button>
              <p className="mt-6 text-[10px] font-cinzel tracking-[0.25em] text-white/30">DISCLAIMER · Submission of this form does not create an advocate-client relationship.</p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
